import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, BadgeDollarSign, MapPin } from "lucide-react";
import { Marquee } from "../components/Marquee";
import { FadeUp } from "../components/Reveal";
import { Underline } from "../components/Logo";
import { GALLERY, IMAGES, MANIFESTO, MARQUEE_ITEMS, SERVICES } from "../lib/data";

const line = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

const TRUST = [
  { icon: ShieldCheck, label: "Fully insured" },
  { icon: BadgeDollarSign, label: "Free, fixed quotes" },
  { icon: MapPin, label: "Moreton Bay to North Brisbane" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="A beautifully maintained modern home at dusk" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/40" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 pt-48 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="font-mono-tech text-[11px] md:text-xs tracking-[0.3em] text-lime-300 uppercase mb-8"
          data-testid="hero-kicker"
        >
          His &amp; Hers — Home Improvements &amp; Property Maintenance
        </motion.p>
        <motion.h1
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } } }}
          className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[1.02] text-zinc-50 tracking-tight"
          data-testid="hero-heading"
        >
          <span className="block overflow-hidden pb-1"><motion.span variants={line} className="block">Your home,</motion.span></span>
          <span className="block overflow-hidden pb-3">
            <motion.span variants={line} className="block">
              <span className="relative inline-block">
                well kept.
                <Underline className="absolute -bottom-2 left-0 w-full h-3" />
              </span>
            </motion.span>
          </span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base md:text-lg text-zinc-300 font-light leading-relaxed"
          data-testid="hero-subtext"
        >
          One friendly team for repairs, carpentry, fencing, decking, landscaping and every odd job in between — for homes and businesses alike.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-wrap gap-4"
        >
          <Link
            to="/about#booking"
            data-testid="hero-book-cta"
            className="group rounded-full bg-lime-400 text-black font-semibold px-8 py-4 flex items-center gap-2 transition-all duration-300 hover:bg-lime-300 hover:-translate-y-1"
          >
            Book a visit
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-services-cta"
            className="rounded-full border border-white/20 text-zinc-100 px-8 py-4 transition-all duration-300 hover:border-lime-400/60 hover:text-lime-300 hover:-translate-y-1"
          >
            Explore services
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
          data-testid="hero-trust-strip"
        >
          {TRUST.map((t) => (
            <span key={t.label} className="flex items-center gap-2.5 font-mono-tech text-[11px] tracking-[0.18em] uppercase text-zinc-400">
              <t.icon size={15} className="text-purple-400" />
              {t.label}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section className="py-32" data-testid="manifesto-section">
    <div className="max-w-7xl mx-auto px-6">
      <FadeUp>
        <p className="font-mono-tech text-xs tracking-[0.3em] text-lime-400 uppercase mb-4">What we stand for</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-zinc-50 mb-24">The way we work</h2>
      </FadeUp>
      <div className="flex flex-col gap-24">
        {MANIFESTO.map((m, i) => (
          <FadeUp key={m.number} delay={0.05 * i} data-testid={`manifesto-chapter-${m.number}`}>
            <div className="flex flex-col md:flex-row md:items-baseline gap-6 md:gap-24 border-t border-white/5 pt-12">
              <span className="font-display text-7xl lg:text-8xl text-transparent bg-clip-text bg-gradient-to-b from-lime-400 to-lime-400/10 shrink-0">
                {m.number}
              </span>
              <div>
                <h3 className="font-display text-3xl lg:text-4xl text-zinc-100 mb-4">{m.title}</h3>
                <p className="text-zinc-400 leading-relaxed max-w-xl text-base md:text-lg font-light">{m.text}</p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ServicesTeaser = () => (
  <section className="py-32 bg-[#0C0E0D] border-y border-white/5" data-testid="services-teaser-section">
    <div className="max-w-7xl mx-auto px-6">
      <FadeUp className="flex flex-wrap items-end justify-between gap-6 mb-16">
        <div>
          <p className="font-mono-tech text-xs tracking-[0.3em] text-lime-400 uppercase mb-4">What we do</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-zinc-50">Six trades, one team</h2>
        </div>
        <Link to="/services" className="group flex items-center gap-2 text-sm text-zinc-300 hover:text-lime-300 transition-colors duration-300" data-testid="services-teaser-link">
          All services
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </FadeUp>
      <div className="grid gap-6 md:grid-cols-3">
        {SERVICES.slice(1, 4).map((s, i) => (
          <FadeUp key={s.id} delay={0.08 * i}>
            <Link
              to="/services"
              data-testid={`teaser-card-${s.id}`}
              className="group relative block h-80 rounded-2xl overflow-hidden border border-white/5 transition-colors duration-500 hover:border-lime-400/40"
            >
              <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute bottom-0 p-7 transition-transform duration-500 group-hover:-translate-y-1">
                <h3 className="font-display text-2xl text-zinc-50 mb-1">{s.short}</h3>
                <p className="text-xs text-zinc-400 line-clamp-2">{s.description}</p>
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Gallery = () => (
  <section className="py-32" data-testid="gallery-section">
    <div className="max-w-7xl mx-auto px-6">
      <FadeUp className="mb-16">
        <p className="font-mono-tech text-xs tracking-[0.3em] text-lime-400 uppercase mb-4">Recent work</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-zinc-50 max-w-3xl">
          Proof, not promises — <span className="italic text-lime-300">straight from the job site.</span>
        </h2>
      </FadeUp>
      <div className="grid gap-6 md:grid-cols-2">
        {GALLERY.map((g, i) => (
          <FadeUp key={g.src} delay={0.06 * i}>
            <figure
              data-testid={`gallery-card-${i}`}
              className="group relative rounded-2xl overflow-hidden border border-white/5 transition-colors duration-500 hover:border-lime-400/40"
            >
              <img
                src={g.src}
                alt={g.title}
                loading="lazy"
                className="w-full h-[26rem] md:h-[30rem] object-cover object-top scale-[1.07] transition-transform duration-700 group-hover:scale-[1.13]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
              <span className="absolute top-5 left-5 rounded-full bg-black/60 backdrop-blur-md border border-purple-400/30 text-purple-300 font-mono-tech text-[10px] tracking-[0.2em] uppercase px-4 py-1.5">
                {g.tag}
              </span>
              <figcaption className="absolute bottom-0 p-7 transition-transform duration-500 group-hover:-translate-y-1">
                <h3 className="font-display text-xl md:text-2xl text-zinc-50 mb-1.5">{g.title}</h3>
                <p className="text-xs md:text-sm text-zinc-400 max-w-md">{g.desc}</p>
              </figcaption>
            </figure>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="py-32 bg-[#0C0E0D] border-t border-white/5" data-testid="home-cta-section">
    <div className="max-w-7xl mx-auto px-6">
      <FadeUp>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl text-zinc-50 leading-tight max-w-4xl">
          Got a list? <span className="italic text-lime-300">We'll bring the tools.</span>
        </h2>
        <Link
          to="/about#booking"
          data-testid="home-cta-book-button"
          className="group mt-12 inline-flex items-center gap-3 rounded-full bg-lime-400 text-black font-semibold px-10 py-5 transition-all duration-300 hover:bg-lime-300 hover:-translate-y-1"
        >
          Book a visit
          <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <main>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <Manifesto />
      <ServicesTeaser />
      <Gallery />
      <CtaBand />
    </main>
  );
}
